import dayjs from 'dayjs'

// Stage color config
export const STAGE_COLORS: Record<
    string,
    { bg: string; text: string; dot: string; ring: string; border: string }
> = {
    intern: {
        bg: 'bg-blue-100',
        text: 'text-blue-700',
        dot: '#3b82f6',
        ring: 'rgba(59,130,246,0.3)',
        border: 'border-l-blue-500',
    },
    probation: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        dot: '#eab308',
        ring: 'rgba(234,179,8,0.3)',
        border: 'border-l-yellow-500',
    },
    official: {
        bg: 'bg-green-100',
        text: 'text-green-700',
        dot: '#22c55e',
        ring: 'rgba(34,197,94,0.3)',
        border: 'border-l-green-500',
    },
    offboard: {
        bg: 'bg-gray-100',
        text: 'text-gray-700',
        dot: '#9ca3af',
        ring: 'rgba(156,163,175,0.3)',
        border: 'border-l-gray-400',
    },
}

export const STAGE_ORDER = ['intern', 'probation', 'official', 'offboard']

export const getStageColor = (stage: string) => {
    const c = STAGE_COLORS[stage]
    return c ? `${c.bg} ${c.text}` : 'bg-gray-100 text-gray-700'
}

/** Check if end_date is actually set (Go zero time "0001-01-01..." is treated as unset) */
export const hasValidEndDate = (date: string | null | undefined): boolean => {
    if (!date) return false
    const d = dayjs(date)
    return d.isValid() && d.year() > 1
}

/** Check if a stage is still active: last stage with no end_date or end_date in future */
export const isStageActive = (date: string | null | undefined): boolean => {
    if (!hasValidEndDate(date)) return true
    return (
        dayjs(date).isAfter(dayjs(), 'day') ||
        dayjs(date).isSame(dayjs(), 'day')
    )
}

/** Calculate days remaining or overdue from end_date */
export const getDaysInfo = (
    endDate: string | null | undefined
): { days: number; isOverdue: boolean } | null => {
    if (!hasValidEndDate(endDate)) return null
    const diff = dayjs(endDate).diff(dayjs(), 'day')
    return { days: Math.abs(diff), isOverdue: diff < 0 }
}

// Framer-motion animation variants
export const cardVariants = {
    hidden: { opacity: 0, x: -24, scale: 0.96 },
    visible: (i: number) => ({
        opacity: 1,
        x: 0,
        scale: 1,
        transition: {
            type: 'spring',
            stiffness: 300,
            damping: 24,
            delay: i * 0.08,
        },
    }),
    exit: {
        opacity: 0,
        x: -24,
        scale: 0.96,
        transition: { duration: 0.2 },
    },
}

export const notesVariants = {
    hidden: { height: 0, opacity: 0 },
    visible: {
        height: 'auto',
        opacity: 1,
        transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
    },
    exit: {
        height: 0,
        opacity: 0,
        transition: { duration: 0.2, ease: [0.4, 0, 1, 1] },
    },
}

export const noteItemVariants = {
    hidden: { opacity: 0, y: -8 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.05, duration: 0.2 },
    }),
}
